import { Auth0Provider } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import Helmet from 'react-helmet';
import { useEffectOnce } from 'react-use';

import GoogleAnalyticsSetUserId from 'components/atoms/GoogleAnalyticsSetUserId';
import NetworkToRedux from 'components/atoms/NetworkToRedux';
import PendoInitialize from 'components/atoms/PendoInitialize';
import Auth0TokenHandler from 'components/miscellaneous/Auth0TokenHandler';
import Routes from 'routes';
import SiteUnderMaintenance from 'routes/PageNotFound/SiteUnderMaintenance';
import { WhiteLabelTitle } from 'types/models/white-label';
import getWhiteLabelConfig from 'utils/get-white-label-config';

const { REACT_APP_AUTH0_DOMAIN, REACT_APP_AUTH0_AUDIENCE, PUBLIC_URL } =
  process.env;

const { title, description, faviconName, auth0ClientId } =
  getWhiteLabelConfig();

function App(): JSX.Element {
  // App initialisation logic.
  useEffectOnce(() => {
    // If this is a Minebot instance, add 'Minebot' class to the root element.
    // This will cause the correct styles to be used.
    if (title === WhiteLabelTitle.MINEBOT) {
      const rootNode = document.getElementById('farmbot');
      if (rootNode) {
        rootNode.className += `${rootNode.className ? ' ' : ''}Minebot`;
      }
    }
  });

  const { showMaintenancePage } = useFlags();

  return (
    <Auth0Provider
      domain={REACT_APP_AUTH0_DOMAIN}
      clientId={auth0ClientId}
      useRefreshTokens
      useRefreshTokensFallback
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: REACT_APP_AUTH0_AUDIENCE
      }}
    >
      <Helmet>
        <title>{`${title}`}</title>
        <meta name={`${title}`} content={`${description}`} />
        <link
          rel="icon"
          type="image/png"
          href={`${PUBLIC_URL}/${faviconName}`}
          sizes="16x16"
        />
      </Helmet>
      <NetworkToRedux />
      <Auth0TokenHandler>
        {showMaintenancePage ? <SiteUnderMaintenance /> : <Routes />}
      </Auth0TokenHandler>
      <GoogleAnalyticsSetUserId />
      <PendoInitialize />
    </Auth0Provider>
  );
}

export default App;
